<template>
    <div class="order-list">
        <v-form ref="form">
            <div class="d-flex align-center">
                <v-text-field
                    v-model="filter"
                    class="mx-2 my-4"
                    :label="$t('general.search')"
                    append-icon="far fa-search"
                    clearable
                    clear-icon="fal fa-times"
                    single-line
                    hide-details
                    @input="filterOrders"
                />

                <v-btn
                    v-if="isOnline"
                    class="ml-1 mr-3 mt-3"
                    color="primary"
                    icon
                    @click="searchMode = !searchMode"
                >
                    <v-icon>fal fa-filter</v-icon>
                </v-btn>
            </div>

            <v-slide-y-transition>
                <div
                    v-if="searchMode"
                    class="pa-4"
                >
                    <v-select
                        v-model="form.user"
                        :items="users"
                        :label="$t('general.staff')"
                        item-text="name"
                        item-value="id"
                        clear-icon="fal fa-times"
                        clearable
                    />

                    <v-row class="mt-1">
                        <v-col class="py-0" cols="8">
                            <v-autocomplete
                                v-model="form.client"
                                :items="clients"
                                item-value="id"
                                item-text="name"
                                :filter="filterClient"
                                :label="$tc('general.buyer', 1)"
                                clear-icon="fal fa-times"
                                clearable
                                :disabled="!!form.clientIdNumber"
                            />
                        </v-col>
                        <v-col class="py-0" cols="4">
                            <v-text-field
                                v-model="form.clientIdNumber"
                                :label="$t('general.tax_id_number')"
                                clearable
                                clear-icon="fal fa-times"
                                :disabled="!!form.client"
                            />
                        </v-col>
                    </v-row>

                    <v-row>
                        <v-col class="py-0" cols="4">
                            <v-select
                                v-model="form.cashRegister"
                                :items="cashRegisters"
                                :label="$t('general.cash_register')"
                                :item-text="item => item.getFullName()"
                                item-value="id"
                                clear-icon="fal fa-times"
                                clearable
                            />
                        </v-col>
                        <v-col class="py-0" cols="4">
                            <v-select
                                v-model="form.status"
                                :items="statuses"
                                item-value="value"
                                item-text="display"
                                :label="$t('orders.receipt_type')"
                                clear-icon="fal fa-times"
                                clearable
                            />
                        </v-col>
                        <v-col class="py-0" cols="4">
                            <v-select
                                v-model="form.paymentType"
                                :items="paymentTypes"
                                item-value="value"
                                item-text="display"
                                :label="$t('general.paid_by')"
                                clear-icon="fal fa-times"
                                :disabled="form.status === 'unpaid' || form.status === 'proforma'"
                                clearable
                            />
                        </v-col>
                    </v-row>

                    <v-row>
                        <v-col class="py-0" cols="8">
                            <v-menu
                                v-model="picker1"
                                :close-on-content-click="false"
                                :nudge-right="40"
                                transition="scale-transition"
                                offset-y
                                min-width="290"
                            >
                                <template v-slot:activator="{ on }">
                                    <v-text-field
                                        v-model="form.startDate"
                                        :error-messages="form.$errors.startDate"
                                        :label="$t('general.start_date')"
                                        prepend-icon="fal fa-calendar"
                                        clear-icon="fal fa-times"
                                        clearable
                                        readonly
                                        v-on="on"
                                        @blur="form.validate('startDate')"
                                        @input="delete form.$errors.startDate"
                                    />
                                </template>
                                <v-date-picker
                                    v-model="form.startDate"
                                    @input="picker1 = false; form.validate('startDate')"
                                />
                            </v-menu>
                        </v-col>

                        <v-col class="py-0" cols="4">
                            <v-text-field
                                v-model="form.startTime"
                                v-mask="'##:##:##'"
                                placeholder="hh:mm:ss"
                                :error-messages="form.$errors.startTime"
                                :label="$t('general.time')"
                                @blur="form.validate('startTime'); form.validate('endTime');"
                                @input="delete form.$errors.startTime"
                            />
                        </v-col>
                    </v-row>

                    <v-row>
                        <v-col class="py-0" cols="8">
                            <v-menu
                                v-model="picker2"
                                :close-on-content-click="false"
                                :nudge-right="40"
                                transition="scale-transition"
                                offset-y
                                min-width="290"
                            >
                                <template v-slot:activator="{ on }">
                                    <v-text-field
                                        v-model="form.endDate"
                                        :error-messages="form.$errors.endDate"
                                        :label="$t('general.end_date')"
                                        prepend-icon="fal fa-calendar"
                                        clear-icon="fal fa-times"
                                        clearable
                                        readonly
                                        v-on="on"
                                        @blur="form.validate('endDate')"
                                    />
                                </template>
                                <v-date-picker
                                    v-model="form.endDate"
                                    :min="form.startDate"
                                    @input="picker2 = false; form.validate('endDate')"
                                />
                            </v-menu>
                        </v-col>

                        <v-col class="py-0" cols="4">
                            <v-text-field
                                v-model="form.endTime"
                                v-mask="'##:##:##'"
                                placeholder="hh:mm:ss"
                                :label="$t('general.time')"
                                :error-messages="form.$errors.endTime"
                                @blur="form.validate('endTime')"
                                @input="delete form.$errors.endTime"
                            />
                        </v-col>
                    </v-row>

                    <div class="d-flex justify-center mt-4">
                        <v-btn
                            color="primary"
                            outlined
                            :loading="busy"
                            @click="getOrders"
                        >
                            {{ $t('general.search') }}
                        </v-btn>
                    </div>
                </div>
            </v-slide-y-transition>
        </v-form>

        <div class="legend-container">
            <v-btn-toggle
                v-if="admin"
                v-model="form.incoming"
                color="primary"
                mandatory
                dense
                group
            >
                <v-btn :value="false">
                    <v-icon size="18" class="mr-2">fal fa-arrow-up</v-icon>
                    {{ $t('order.outgoing') }}
                </v-btn>
                <v-btn :value="true">
                    <v-icon size="18" class="mr-2">fal fa-arrow-down</v-icon>
                    {{ $t('order.incoming') }}
                </v-btn>
            </v-btn-toggle>

            <v-tooltip
                v-if="admin && form.incoming && einvoiceEnabled && incomingOrdersRefreshing !== 'done'"
                right
                color="primary"
            >
                <template v-slot:activator="{ on }">
                    <v-btn
                        v-on="on"
                        icon
                        small
                        class="ml-1 mt-2"
                        color="primary"
                        :loading="incomingOrdersRefreshing === 'inProgress'"
                        @click.stop.prevent="refreshAllOrders"
                    >
                        <v-icon size="16">far fa-redo</v-icon>
                    </v-btn>
                </template>
                <span>{{ $t('order.refresh_einvoices') }}</span>
            </v-tooltip>

            <v-spacer />

            <div class="legend">
                <template v-if="locale === 'me'">
                    <div class="legend-item summary"></div> {{ $t('orders.status.summary') }}
                </template>

                <template v-if="!isAllegra">
                    <div class="legend-item unpaid-order"></div> {{ $t('general.unpaid') }}
                </template>

                <div class="legend-item canceled"></div> {{ $t('orders.status.canceled') }}
                <template v-if="!noncashOnly">/ {{ $t('orders.status.corrective') }}</template>

                <div class="legend-item other"></div>
                {{ $t('orders.status.advance') }} / {{ $t('orders.status.proforma') }} / {{ $t('orders.status.credit_note') }}
                <template v-if="locale === 'sr' && !noncashOnly">
                    / {{ $t('orders.status.copy') }} / {{ $t('orders.status.training') }}
                </template>
            </div>
        </div>

        <v-data-table
            v-model="selectedInternal"
            class="data-table striped"
            :headers="headers"
            :items="ordersInternal"
            :options.sync="pagination"
            :footer-props="{'items-per-page-options': [10, 20, 50, 100, 200]}"
            :items-per-page.sync="pagination.itemsPerPage"
            :server-items-length="total"
            :mobile-breakpoint="0"
            :loading="busy"
            :show-select="!!selectMode"
        >
            <template
                v-if="!!selectMode"
                v-slot:header.data-table-select="{ on, props }"
            >
                <div></div>
            </template>

            <template v-slot:item="{ isSelected, select, item }">
                <tr
                    class="item"
                    :class="itemClass(item)"
                    @click="onRowClick(item)"
                >
                    <td v-if="!!selectMode">
                        <v-simple-checkbox
                            v-if="canBeSelected(item)"
                            color="primary"
                            :ripple="false"
                            :value="isSelected"
                            @input="select($event);"
                        />
                    </td>

                    <td>
                        <div class="d-flex align-center">
                            <v-tooltip
                                v-if="item.alert"
                                right
                                :color="item.alert.color"
                            >
                                <template v-slot:activator="{ on }">
                                    <v-icon
                                        class="mr-2"
                                        :color="item.alert.color"
                                        size="16"
                                        v-on="on"
                                    >
                                        far fa-info-circle
                                    </v-icon>
                                </template>
                                <span>{{ item.alert.message }}</span>
                            </v-tooltip>

                            <div>
                                <span v-if="!item.isIncoming()">{{ item.number }}</span>
                                <span v-else>{{ item.internal_number }}</span>

                                <span v-if="item.register_response && item.register_response.invoiceCounter">
                                    ({{ item.register_response.invoiceCounter }})
                                </span>
                                <span v-if="item.isEinvoice()">
                                    / {{ $tc('general.einvoice', 1) }}
                                </span>
                            </div>
                        </div>
                    </td>

                    <td>
                        {{ item.getLastActivityAt() | dateTimeHuman }}
                        <v-tooltip
                            v-if="item.due_date && moment(item.due_date).isBefore(moment())"
                            right
                            color="error"
                        >
                            <template v-slot:activator="{ on }">
                                <v-icon
                                    class="ml-2"
                                    color="error"
                                    size="16"
                                    v-on="on"
                                >
                                    fal fa-calendar-exclamation
                                </v-icon>
                            </template>
                            <span>{{ $t('general.due_date_expired') }}</span>
                        </v-tooltip>
                    </td>

                    <td>
                        <div class="d-flex align-center">
                            <div>{{ getRegisterStatusMsg(item) }}</div>
                            <v-tooltip
                                v-if="allowRefreshOrder(item)"
                                right
                                color="primary"
                            >
                                <template v-slot:activator="{ on }">
                                    <v-btn
                                        v-on="on"
                                        icon
                                        small
                                        class="ml-1"
                                        color="primary"
                                        :loading="item.refreshing === 'inProgress'"
                                        @click.stop.prevent="refreshOrder(item)"
                                    >
                                        <v-icon size="16">far fa-redo</v-icon>
                                    </v-btn>
                                </template>
                                <span>{{ $t('order.refresh_status_einvoice') }}</span>
                            </v-tooltip>
                        </div>
                    </td>

                    <td>{{ item.user ? item.user.name : '' }}</td>
                    <td>{{ item.client ? item.client.name : '' }}</td>

                    <template v-if="!isAllegra">
                        <td>{{ item.table ? item.sector.name : '' }}</td>
                        <td>{{ item.table ? item.table.name : '' }}</td>
                    </template>

                    <td>{{ item.paymentType() }}</td>
                    <td class="to-pay">
                        <v-tooltip
                            v-if="item.pricelist"
                            left
                            color="error"
                        >
                            <template v-slot:activator="{ on }">
                                <v-icon
                                    class="mr-2"
                                    color="error"
                                    size="16"
                                    v-on="on"
                                >
                                    far fa-info-circle
                                </v-icon>
                            </template>
                            <span>{{ $tc('general.pricelist', 1) }}: {{ item.pricelist }}</span>
                        </v-tooltip>
                        {{ item.getToPay().toFormat('0.00') }}
                    </td>
                    <td align="center" class="w35 maxw60">
                        <v-menu
                            v-if="item.isIncoming() && item.flagsInclude('manual')"
                            bottom
                            transition="slide-y-transition"
                        >
                            <template v-slot:activator="{ on }">
                                <v-btn v-on="on" icon>
                                    <v-icon>far fa-ellipsis-h</v-icon>
                                </v-btn>
                            </template>

                            <v-list>
                                <v-list-item
                                    :key="1"
                                    @click="openItemForm(item)"
                                >
                                    <v-list-item-action class="mr-4 flex justify-content-center">
                                        <v-icon>fal fa-pen</v-icon>
                                    </v-list-item-action>
                                    <v-list-item-title>{{ $t('general.edit') }}</v-list-item-title>
                                </v-list-item>

                                <v-divider />

                                <v-list-item
                                    :key="2"
                                    @click="deleteItem(item)"
                                >
                                    <v-list-item-action class="mr-4 flex justify-content-center">
                                        <v-icon class="error--text">fal fa-times</v-icon>
                                    </v-list-item-action>
                                    <v-list-item-title class="error--text">
                                        {{ $t('general.delete') }}
                                    </v-list-item-title>
                                </v-list-item>
                            </v-list>
                        </v-menu>
                    </td>
                </tr>
            </template>
        </v-data-table>
    </div>
</template>

<script>
import ApiService from '@/services/ApiService'
import EinvoiceService from '@/services/EinvoiceService';
import User from '@/models/User'
import Client from '@/models/Client'
import Sector from '@/models/Sector'
import Table from '@/models/Table'
import CashRegister from '@/models/CashRegister'
import Pricelist from '@/models/Pricelist'
import Shift from '@/models/Shift'
import _ from 'lodash';
import { helpers } from 'vuelidate/lib/validators';
import * as rules from 'vuelidate/lib/validators'
import { mapGetters } from 'vuex';

export default {
    props: {
        selectMode: {
            type: String,
            default: null
        },
        orders: {
            type: Array,
            default: () => []
        },
        selected: {
            type: Array,
            default: () => []
        },
        users: {
            type: Array,
            default: () => []
        },
        clients: {
            type: Array,
            default: () => []
        },
        cashRegisters: {
            type: Array,
            default: () => []
        },
        admin: {
            type: Boolean,
            default: false
        }
    },

    data() {
        return {
            headers: this.createHeaders(),

            pagination: {
                sortBy: ['last_activity_at'],
                itemsPerPage: 10,
                page: 1,
                sortDesc: [true],
            },

            form: this.createForm(),
            searchMode: false,
            picker1: false,
            picker2: false,
            busy: false,

            ordersInternal: [],
            filter: null,
            total: 0,

            selectedInternal: [],
            allOrders: [],
            incomingOrdersRefreshing: null,
        };
    },

    validations() {
        return {
            form: {
                startDate: { required: rules.required },
                startTime: {
                    required: rules.required,
                    time: helpers.withParams(
                        { type: 'time' },
                        function(value) {
                            if(!value) return true

                            return moment(value, 'HH:mm:ss', true).isValid()
                        }
                    )
                },
                endDate: { required: rules.required },
                endTime: {
                    required: rules.required,
                    timeAfter: helpers.withParams(
                        { type: 'timeAfter' },
                        function(value) {
                            if(!value) return true

                            if(!moment(value, 'HH:mm:ss', true).isValid()) {
                                return false
                            }

                            if(!this.form.startDate || !this.form.startTime || !this.form.endDate) {
                                return false
                            }

                            return moment(`${this.form.endDate} ${value}`)
                                .isAfter(moment(`${this.form.startDate} ${this.form.startTime}`))
                        }
                    )
                },
            }
        }
    },

    watch: {
        pagination: {
            handler() {
                if(!this.busy) {
                    this.getOrders();
                }
            },

            deep: true
        },

        'form.startDate'(val) {
            this.form.startTime = val ? '00:00:00' : null
            this.form.endDate = null;
            this.form.endTime = null;
            this.form.validate()
        },

        'form.endDate'(val) {
            this.form.endTime = val ? '00:00:00' : null
            this.form.validate()
        },

        'form.status'(val) {
            if(val === 'unpaid' || val === 'proforma') {
                this.form.paymentType = null
            }
        },

        'form.client'(val) {
            if(val) {
                this.form.clientIdNumber = null
            }
        },

        'form.clientIdNumber'(val) {
            if(val) {
                this.form.client = null
            }
        },

        'form.incoming'(val) {
            if(!this.busy) {
                this.getOrders();
            }
        },

        orders(val) {
            this.ordersInternal = val
        },

        ordersInternal(val) {
            this.$emit('update:orders', val)
        },

        selected(val) {
            this.selectedInternal = val
        },

        selectedInternal(val) {
            this.$emit('update:selected', val)
        },

        selectMode(val) {
            if(val === 'summaryCreditNote') {
                this.selectedInternal = this.ordersInternal.filter(order => this.canBeSelected(order, 'creditNote'))
                return;
            }

            this.selectedInternal = []
        },

        allowedSelectModes(val) {
            this.$emit('update:allowedSelectModes', val)
        },
    },

    computed: {
        ...mapGetters(['trainingMode', 'integrations']),

        statuses() {
            let statuses = ['advance', 'canceled', 'proforma', 'credit_note', 'corrective'];

            if(locale === 'me') {
                statuses = ['paid', 'unpaid', 'summary', ...statuses];
            } else if(locale === 'sr' && !EinvoiceService.einvoiceOnly()) {
                statuses = [...statuses, 'copy', 'training'];
                if(EinvoiceService.isEnabled()) {
                    statuses = [...statuses, 'einvoice'];
                }
            }

            return statuses.map(status => {
                return {
                    value: status,
                    display: this.$t(`orders.status.${status}`),
                }
            })
        },

        paymentTypes() {
            const paymentTypes = CashRegister.availablePaymentTypes()

            if(this.integrations.ota_sync) {
                paymentTypes.push({ value: 'send_to_room', display: this.$t('integrations.ota_sync.room_send_payment') })
            }

            return paymentTypes
        },

        dateFilteringActive() {
            return !!this.form.startDate || !!this.form.startTime || !!this.form.endDate || !!this.form.endTime
        },

        allowedSelectModes() {
            if(this.trainingMode) {
                return {
                    paySummary: false,
                    creditNote: false,
                    summaryCreditNote: false,
                    advance: false,
                }
            }

            const selectablePaySummaryOrders = this.ordersInternal.filter(order => this.canBeSelected(order, 'paySummary'));
            const selectableCreditNoteOrders = this.ordersInternal.filter(order => this.canBeSelected(order, 'creditNote'));
            const selectableAdvanceOrders = this.ordersInternal.filter(order => this.canBeSelected(order, 'advance'));

            const curShift = Shift.current()

            const clientId = selectableCreditNoteOrders[0]?.client_id
            const idNumber = selectableCreditNoteOrders[0]?.client?.id_number
            const taxIdNumber = selectableCreditNoteOrders[0]?.client?.tax_id_number
            const allSelectableCreditNoteOrdersWithSameClient = clientId && selectableCreditNoteOrders.every(order => {
                return order.client_id && order.client_id === clientId
                    || order?.client.id_number && order?.client.id_number === idNumber
                    || order?.client.tax_id_number && order?.client.tax_id_number === taxIdNumber
            })

            return {
                paySummary: isAllegra && !!curShift && selectablePaySummaryOrders.length > 0,
                creditNote: !!curShift && selectableCreditNoteOrders.length > 0,
                summaryCreditNote: !!curShift && allSelectableCreditNoteOrdersWithSameClient,
                advance: !!curShift && selectableAdvanceOrders.length > 0,
            }
        },

        noncashOnly() {
            return !!this.tenant?.venue?.settings?.noncash_only;
        },

        einvoiceEnabled() {
            return EinvoiceService.isEnabled();
        },
    },

    methods: {
        getOrders() {
            if (!this.form.validate()) return;

            if(!this.isOnline) {
                this.getOrdersOffline()
                return
            }

            const { sortBy, sortDesc, page, itemsPerPage } = this.pagination
            const parameters = {
                sortField: sortBy[0] ? sortBy[0] : 'last_activity_at',
                sortOrder: sortDesc[0] ? 'descending' : 'ascending',
                itemsPerPage: itemsPerPage,
                page: page,
                incoming: this.form.incoming
            }

            if(this.filter) {
                parameters.filter = this.filter
            }

            if (this.form.client) {
                parameters.client = this.form.client;
            }

            if (this.form.clientIdNumber) {
                parameters.clientIdNumber = this.form.clientIdNumber;
            }

            if (this.form.status) {
                parameters.status = this.form.status;
            }

            if (this.form.paymentType) {
                parameters.paymentType = this.form.paymentType;
            }

            if (this.form.user) {
                parameters.user = this.form.user;
            }

            if (this.form.cashRegister) {
                parameters.cashRegister = this.form.cashRegister;
            }

            parameters.startDate = `${this.form.startDate} ${this.form.startTime}`;
            parameters.endDate = `${this.form.endDate} ${this.form.endTime}`;

            const url = this.admin ? '/admin/orders' : '/orders'

            this.busy = true;
            ApiService.get(url, { params: parameters }).then(response => {
                this.busy = false;
                this.searchMode = false
                this.total = response.data.total;
                this.ordersInternal = response.data.data.map(order => this.mapOrder(order));
            });
        },

        getOrdersOffline() {
            const filter = this.filter ? this.filter.toLowerCase() : null;
            const { sortBy, sortDesc, page, itemsPerPage } = this.pagination

            const users = User.all()
            const clients = Client.all()
            const tables = Table.all()
            const sectors = Sector.all()

            this.allOrders = Order.all()
                .map(order => {
                    order.user = users.find(user => order.user_id == user.id)
                    order.client = clients.find(client => order.client_id == client.id)

                    const table = tables.find(table => order.table_id == table.id)
                    if(table) {
                        const sector = sectors.find(sector => table.sector_id == sector.id)
                        order.sector = new Sector(sector.$toJson())
                    }

                    order.table = table

                    return order
                })

            const sortField = sortBy[0] ? sortBy[0] : 'last_activity_at'
            const sortDirection = sortDesc[0] ? 'desc' : 'asc'

            const orders = this.allOrders
                .filter(order => {
                    if(this.form.incoming !== order.isIncoming()) return false;

                    if(!filter) return true;

                    const paidByOrder = order.paidByOrder();

                    return order.getToPay().toFormat().includes(filter)
                        || order.number && ('' + order.number).toLowerCase().includes(filter)
                        || order.register_number && order.register_number.toLowerCase().includes(filter)
                        || order.register_response && order.register_response.invoiceCounter.toLowerCase().includes(filter)
                        || order.user && order.user.name.toLowerCase().includes(filter)
                        || order.iic && order.iic.toLowerCase().includes(filter)
                        || order.client && order.client.name.toLowerCase().includes(filter)
                        || order.client && order.client.tax_id_number && order.client.tax_id_number.toLowerCase() === filter
                        || order.client && order.client.id_number && order.client.id_number.toLowerCase() === filter
                        || paidByOrder && paidByOrder.iic && paidByOrder.iic.toLowerCase().includes(filter)
                        || (order.table && order.table.name.toLowerCase().includes(filter))
                        || (order.sector && order.sector.name.toLowerCase().includes(filter))
                        || order.items.some(
                            orderItem => orderItem.name.toLowerCase().includes(filter)
                        )
                }).sort((a, b) => {
                    if (a[sortField] < b[sortField]) {
                        return sortDirection === 'desc' ? 1 : -1;
                    }
                    if (a[sortField] > b[sortField]) {
                        return sortDirection === 'desc' ? -1 : 1;
                    }
                    return 0;
                }).slice((page - 1) * itemsPerPage, page * itemsPerPage);

            this.ordersInternal = orders

            this.total = this.allOrders.length;
        },

        createForm() {
            const noncashOnly = !!this.tenant?.venue?.settings?.noncash_only;
            const cashRegister = !this.admin ? CashRegister.current() : null;
            const startDate = noncashOnly
                ? moment().subtract(1, 'month').format('YYYY-MM-DD')
                : moment().subtract(1, 'day').format('YYYY-MM-DD')

            return new Form(this, 'form', {
                startDate: startDate,
                startTime: '00:00:00',
                endDate: moment().format('YYYY-MM-DD'),
                endTime: '23:59:59',
                client: null,
                clientIdNumber: null,
                status: null,
                paymentType: null,
                user: null,
                cashRegister: cashRegister ? cashRegister.id : null,
                incoming: false
            });
        },

        createHeaders() {
            let headers = [];
            let numberHeader = this.$t('general.number');
            if(locale === 'sr' && !EinvoiceService.einvoiceOnly()) {
                numberHeader += ` (${this.$t('general.sdc_short')} ${this.$t('sdc.counter')})`;
            }

            headers.push({ text: numberHeader, value: 'number' });
            headers.push({ text: this.$t('general.time'), value: 'last_activity_at' });
            headers.push({ text: this.$t('general.status'), value: 'status' });
            headers.push({ text: this.$t('general.staff'), value: 'user' });
            headers.push({ text: this.$tc('general.buyer', 1), value: 'client' });

            if (!this.isAllegra) {
                headers.push({ text: this.$t('general.sector'), value: 'sector' });
                headers.push({ text: this.$t('general.table'), value: 'table' });
            }

            headers.push({ text: this.$t('general.paid_by'), value: 'payment_type' });
            headers.push({ text: this.$t('general.to_pay'), value: 'to_pay', class: 'to-pay' });
            headers.push({ text: '', value: 'actions', sortable: false });

            return headers;
        },

        itemClass(order) {
            return {
                'summary': order.isSummary() && !order.corrected() && !order.canceled(),
                'approved': order.einvoiceIsApproved(),
                'unpaid-order': order.unpaidNoSummary() && !order.corrected() && !order.correctsOthers() && !order.canceled(),
                'canceled': order.corrected() || (order.correctsOthers() && !order.isSummary()) || order.canceled()
                    || (order.einvoiceIsFailed() || order.einvoiceIsRejected() || order.einvoiceIsCancelled() || order.einvoiceIsStorno())
                    || order.getToPay().toUnit() === 0,
                'other': order.isAdvance() || order.isProforma() || order.isCreditNote() || order.isCopy() || order.isTraining(),
            }
        },

        onRowClick(order) {
            if(!this.selectMode) {
                this.$emit('click', order)
                return
            }

            if(!this.canBeSelected(order)) {
                return
            }

            const alreadySelected = this.selectedInternal.find(o => o.hash === order.hash)
            if(alreadySelected) {
                this.selectedInternal = this.selectedInternal.filter(o => o.hash !== order.hash)
            } else {
                this.selectedInternal.push(order)
            }
        },

        canBeSelected(order, mode = this.selectMode) {
            // TODO uncomment when register_type is backfilled
            // if(this.selectedInternal.length > 0 && this.selectedInternal[0].register_type !== order.register_type) {
            //     return false;
            // }

            // TODO remove when register_type is backfilled
            if(this.selectedInternal.length > 0 && !!this.selectedInternal[0].is_einvoice !== !!order.is_einvoice) {
                return false;
            }

            if(order.isEinvoice() && !order.einvoiceIsApproved()) return false;

            let isSameClient = true;

            if(
                this.selectedInternal.length > 0
                && this.selectedInternal[0].client_id !== order.client_id
                && this.selectedInternal[0].client?.id_number !== order.client?.id_number
                && this.selectedInternal[0].client?.tax_id_number !== order.client?.tax_id_number
            ) {
                isSameClient = false;
            }

            if(mode === 'advance') {
                const advanceSelectable = order.isAdvance() && !order.corrected() && !order.correctsOthers() && order.getToPay().toUnit() > 0;

                if(!order.isEinvoice()) return advanceSelectable;

                return advanceSelectable && isSameClient;
            }

            if(!order.client_id) return false;

            if(!isSameClient) return false;

            if(mode === 'paySummary') {
                return order.unpaidNoSummary() && !order.corrected() && !order.correctsOthers();
            } else if(mode === 'creditNote') {
                return (order.isPaid() || (locale === 'me' && order.isUnpaid())) && !order.corrected() && !order.correctsOthers();
            } else if(mode === 'summaryCreditNote') {
                return (order.isPaid() || (locale === 'me' && order.isUnpaid())) && !order.corrected() && !order.correctsOthers();
            }

            return false
        },

        filterOrders: _.debounce(function() {
            this.getOrders();
        }, 500),

        filterClient(item, queryText, itemText) {
            return item.matches(queryText);
        },

        openItemForm(item = null) {
            if(!item) {
                this.$router.push({ name: 'adminOrderCreate' }).catch(err => {});
                return;
            }

            this.$router.push({ name: 'adminOrderUpdate', params: { id: item.id } }).catch(err => {});
        },

        deleteItem(item) {
            this.$confirm(
                this.$t('order_edit.permanently_delete'),
                {
                    buttonTrueText: this.$t('general.confirm'),
                    icon: 'fal fa-times',
                    color: 'error',
                    title: this.$t('general.delete')
                },
                async result => {
                    if (!result) return;

                    await ApiService.delete(`/admin/orders/${item.id}`, {})

                    this.$snackbar(this.$t('order_edit.deleted'), 'success');

                    this.getOrders();
                }
            );
        },

        mapOrder(order) {
            const mappedOrder = new Order(order);

            mappedOrder.user = order.user ? new User(order.user) : null;
            mappedOrder.client = order.client ? new Client(order.client) : null;
            mappedOrder.table = order.table ? new Table(order.table) : null;
            mappedOrder.refreshing = null;

            if(mappedOrder.isProforma() && mappedOrder.ordersFromProforma().length) {
                mappedOrder.alert = {
                    color: 'info',
                    message: this.$t('orders.alerts.proforma_receipt_issued')
                }
            } else if(mappedOrder.isAdvance() && mappedOrder.ordersFromAdvance().length) {
                mappedOrder.alert = {
                    color: 'error',
                    message: this.$t('orders.alerts.advance_receipt_issued')
                }
            }

            if(mappedOrder.table) {
                mappedOrder.sector = order.table.sector ? new Sector(order.table.sector) : null;
            }

            if(mappedOrder.getPricelist()) {
                const pricelist = Pricelist.find(mappedOrder.getPricelist())
                mappedOrder.pricelist = pricelist?.name || null;
            }

            return mappedOrder;
        },

        allowRefreshOrder(order) {
            return order.isEinvoice()
                && order.register_number
                && order.refreshing !== 'done'
                && !order.einvoiceIsRejected()
                // && !order.einvoiceIsCancelled()
                // && !order.einvoiceIsStorno()
        },

        async refreshOrder(order) {
            if (!this.isOnline) {
                this.$snackbar(this.$t('connection.internet_offline_message_not_allowed'), 'error', { timeout: 10000 });
                return;
            }

            order.refreshing = 'inProgress';

            const result = await order.einvoiceRefresh();

            order.refreshing = result.error ? 'failed' : 'done';

            if(!result.success) {
                this.$snackbar(this.$t('general.action_failed'), 'error');
                return;
            }
        },

        async refreshAllOrders() {
            this.incomingOrdersRefreshing = 'inProgress';

            if (!this.isOnline) {
                this.$snackbar(this.$t('connection.internet_offline_message_not_allowed'), 'error', { timeout: 10000 });
                return;
            }

            const response = await ApiService.get('/orders/einvoice/sync-incoming');
            this.incomingOrdersRefreshing = 'done';

            if(!response.data.success) {
                this.$snackbar(this.$t('general.action_failed'), 'error');
                return;
            }

            if(response.data.number_of_invoices == 0) {
                this.$snackbar(this.$t('einvoice.detected_new_none'));
                return;
            }

            this.$snackbar(this.$t('einvoice.detected_new', { value: response.data.number_of_invoices }), 'success');
            this.getOrders();
        },

        getRegisterStatusMsg(order) {
            const translatePath = !order.isEinvoice()
                ? `order.register_status.fiscal.${order.register_status}`
                : `order.register_status.einvoice.${order.register_status}`;

            const statusMsg = this.$t(translatePath);

            return statusMsg !== translatePath ? statusMsg : '';
        }
    }
};
</script>

<style lang="scss" scoped>
.order-list {
    ::v-deep th, td {
        padding: 0 10px !important;
    }

    .v-input--selection-controls {
        padding-top: 0;
        margin-top: 0;
    }

    .legend-container {
        display: flex;
        margin-bottom: 12px;
        flex-wrap: wrap;

        .legend {
            display: flex;
            align-items: center;
            justify-content: flex-end;
            padding: 5px;

            .legend-item {
                min-height: 10px;
                min-width: 10px;
                border-radius: 5px;
                margin: 0 5px 0 10px;

                &.summary {
                    background: var(--v-success-base);
                }
                &.approved {
                    background: var(--v-success-base);
                }
                &.unpaid-order {
                    background: var(--v-warning-base);
                }
                &.canceled {
                    background: var(--v-error-base) !important;
                }
                &.other {
                    background: var(--v-primary-base);
                }
            }
        }
    }

    ::v-deep .data-table {
        .item {
            cursor: pointer;

            /* Enable text selection */
            -webkit-user-select: text;
            -webkit-app-region: none;

            &.summary {
                color: var(--v-success-base);
            }
            &.approved {
                color: var(--v-success-base);
            }
            &.unpaid-order {
                color: var(--v-warning-base);
            }
            &.canceled {
                color: var(--v-error-base) !important;
            }
            &.other {
                color: var(--v-primary-base);
            }

            .to-pay {
                text-align: right !important;
                padding: 0 10px !important;
                width: 90px;
            }
        }
    }
}
</style>
